import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Waveform, Ring2 } from 'ldrs/react';
import { message, Upload, Flex, Space, Tooltip } from 'antd';
import {
    CloudUploadOutlined,
    CloseOutlined,
    LoadingOutlined,
    PictureTwoTone,
    PaperClipOutlined,
    FileTwoTone
} from '@ant-design/icons';
import { s3UploadApi } from '@@service/s3Upload';
import { trackEvent } from '@/service/tracking';
import { useAudioRecorder, useDevice, useThemeToken } from '@/hooks';
import type { UploadFile, UploadProps } from 'antd';
import pdfFileIcon from '@/assets/icons/pdf-file.svg';
import AntdButton from '../button';
import SvgIcon from '../svg-icon';
import 'ldrs/react/Ring2.css';
import 'ldrs/react/Waveform.css';
import './index.less';

const recordingTip = 'Voice input in progress…';

interface UserInputProps {
    placeholder?: string;
    // eslint-disable-next-line no-unused-vars
    onSubmit: (value: string, attachments?: string[]) => void;
    // eslint-disable-next-line no-unused-vars, max-len
    onChange?: ((value: string, event?: React.FormEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLTextAreaElement> | undefined) => void);
    loading?: boolean;
    disabled?: boolean;
    style?: React.CSSProperties;
    className?: string;
    inputValue?: string;
    maxLength?: number;
    maxFileCount?: number;
}

type HeaderType = '' | 'link' | 'mic';

const UserInput: React.FC<UserInputProps> = ({
    placeholder = 'Type your question here…',
    loading = false,
    disabled = false,
    style,
    className = '',
    inputValue = '',
    onChange,
    onSubmit,
    maxLength = 400,
    maxFileCount = 5
}) => {
    const [ inputText, setInputText ] = useState('');
    const [ headerStatus, setHeaderStatus ] = useState<{ open: boolean; type: HeaderType }>({ open: false, type: '' });
    const [ fileList, setFileList ] = useState<UploadFile[]>([]);
    const [ recording, setRecording ] = React.useState(false);
    const [ tempRecordingText, setTempRecordingText ] = useState('');
    const throttleTimer = useRef<any>(null);
    const inputRef = useRef<HTMLTextAreaElement | null>(null);
    const curTheme = useSelector((state: any) => state.theme);
    const {
        colorPrimary,
        colorReferencePrimary,
        colorTextDisabled,
        colorBtnText
    } = useThemeToken();
    const layoutMode = useDevice();
    const {
        start,
        stop,
        text: recordingText
    } = useAudioRecorder({ autoStop: false });

    const isMobile = layoutMode.isMobile;

    const closeHeader = () => setHeaderStatus({ open: false, type: '' });

    const sendButtonDisabled = disabled || (inputText.length === 0 && fileList.length === 0);

    const showRecordingPlaceholder = recording && !(headerStatus.open && headerStatus.type === 'mic');

    const toggleHeader = (type: HeaderType) => {
        if (!type) {
            closeHeader();
            return;
        }
        setHeaderStatus(prev => {
            const shouldOpen = !(prev.type === type && prev.open);
            return {
                open: shouldOpen,
                type: shouldOpen ? type : ''
            };
        });
    };

    // 处理文件上传前的验证
    function beforeUpload(file: File) {
        const isImage = file.type.startsWith('image/');
        const isPdf = file.type === 'application/pdf';
        const isLt10M = file.size / 1024 / 1024 < 10;
        if (fileList.length >= maxFileCount) {
            message.error(`You can upload up to ${maxFileCount} files.`);
            return false;
        }
        if (!isImage && !isPdf) {
            message.error('Only images and PDF files are allowed!');
            return false;
        }
        if (!isLt10M) {
            message.error('File size cannot exceed 10MB!');
            return false;
        }
        return false;
    };

    // 处理文件列表变化
    function handleFileChange({ fileList: newFileList }: { fileList: UploadFile[] }) {
        setFileList(newFileList);
    };

    const handleRecordingToggle = () => {
        const TrackingParams = {
            eventName: 'VoiceInputStart',
            eventData: {}
        };
        if (recording) {
            TrackingParams.eventName = 'VoiceInputEnd';
            trackEvent(TrackingParams);
            stop();
            setRecording(false);
        } else {
            TrackingParams.eventName = 'VoiceInputStart';
            trackEvent(TrackingParams);
            start();
            setRecording(true);
        }
    };

    // 处理发送消息
    const handleSend = async () => {
        if (!inputText.trim() && fileList.length === 0) return;

        const fileKeys: string[] = [];
        const guessContentType = (name: string, fallback: string) => {
            const lower = name.toLowerCase();
            if (lower.endsWith('.png')) return 'image/png';
            if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
            if (lower.endsWith('.gif')) return 'image/gif';
            if (lower.endsWith('.webp')) return 'image/webp';
            if (lower.endsWith('.heic')) return 'image/heic';
            if (lower.endsWith('.bmp')) return 'image/bmp';
            if (lower.endsWith('.svg')) return 'image/svg+xml';
            if (lower.endsWith('.pdf')) return 'application/pdf';
            if (lower.endsWith('.html') || lower.endsWith('.htm')) return 'text/html';
            return fallback;
        };

        for (const file of fileList) {
            if (!file.originFileObj) continue;
            try {
                const fileObj = file.originFileObj as File;
                const contentType = guessContentType(
                    file.name,
                    fileObj.type || 'application/octet-stream'
                );
                const presign: any = await s3UploadApi.generateUploadUrl(file.name, contentType);
                if (!presign?.upload_url || !presign?.file_key) {
                    message.error(`File upload failed: ${file.name}`);
                    // 埋点：上传失败（获取预签名URL失败）
                    trackEvent({
                        eventName: 'AttachmentUpload',
                        eventData: {
                            fileName: file.name,
                            fileType: contentType,
                            status: 'fail',
                            errorMessage: 'Failed to get presigned URL'
                        }
                    });
                    return;
                }
                const uploadResult = await s3UploadApi.uploadFileToS3(
                    presign.upload_url,
                    fileObj,
                    (percent: number) => {
                        file.percent = percent;
                        setFileList((prev) => prev.map((f) => (f.uid === file.uid ? { ...f, percent } : f)));
                    },
                    { 'Content-Type': contentType }
                );

                // 埋点：根据上传结果记录
                if (uploadResult.success) {
                    fileKeys.push(presign.file_key);
                    trackEvent({
                        eventName: 'AttachmentUpload',
                        eventData: {
                            fileName: file.name,
                            fileType: contentType,
                            status: 'success',
                            uploadDurationMs: uploadResult.uploadDurationMs
                        }
                    });
                } else {
                    message.error(`File upload failed: ${file.name}`);
                    trackEvent({
                        eventName: 'AttachmentUpload',
                        eventData: {
                            fileName: file.name,
                            fileType: contentType,
                            status: 'fail',
                            errorCode: uploadResult.status,
                            errorMessage: uploadResult.message || uploadResult.statusText,
                            uploadDurationMs: uploadResult.uploadDurationMs
                        }
                    });
                    return;
                }
            } catch (error: any) {
                message.error(`File upload failed: ${file.name}`);
                // 埋点：上传失败（未预期的异常）
                trackEvent({
                    eventName: 'AttachmentUpload',
                    eventData: {
                        fileName: file.name,
                        fileType: file.originFileObj?.type || 'application/octet-stream',
                        status: 'fail',
                        errorMessage: error?.message || 'Unknown error'
                    }
                });
                return;
            }
        }

        onSubmit(inputText, fileKeys.length > 0 ? fileKeys : undefined);
        setInputText('');
        closeHeader();
        setFileList([]);
    };

    const onInputChange = (v, e) => {
        let val = v;
        if (maxLength && val.length > maxLength) {
            val = val.slice(0, maxLength);
        }
        onChange && onChange(val, e);
        setInputText(val);
    };

    const throttle = (fn, delay) => {
        return () => {
            if (!throttleTimer.current) {
                fn();
                throttleTimer.current = setTimeout(() => {
                    throttleTimer.current = null;
                }, delay);
            }
        };
    };

    const throttleSend = throttle(() => {
        handleSend();
    }, 500);

    const isPdfUploadFile = (file?: UploadFile) => {
        if (!file) return false;
        if (file.type && file.type.toLowerCase().includes('pdf')) {
            return true;
        }
        return !!file.name && file.name.toLowerCase().endsWith('.pdf');
    };

    const renderUploadIcon: UploadProps['iconRender'] = (file, listType) => {
        if (isPdfUploadFile(file)) {
            return (
                <img
                    src={pdfFileIcon}
                    alt="PDF file"
                    className="user-input-upload-icon"
                    draggable={false}
                />
            );
        }
        const isPictureList = listType && (listType === 'picture' || listType === 'picture-card' || listType === 'picture-circle');
        if (file.status === 'uploading') {
            return isPictureList ? <LoadingOutlined /> : <PaperClipOutlined />;
        }
        if (isPictureList) {
            return <PictureTwoTone />;
        }
        return <FileTwoTone />;
    };

    // 附件上传弹窗浮层（PC模式菜单、移动端下沉）
    const uploadContent = (
        <Upload
            beforeUpload={beforeUpload}
            onChange={handleFileChange}
            fileList={fileList}
            maxCount={maxFileCount}
            // accept="image/*,application/pdf,text/html,.html,.htm"
            accept="image/*,application/pdf"
            // showUploadList={fileList.length > 0}
            listType="picture-card"
            multiple
            showUploadList={fileList.length > 0 ? { showPreviewIcon: false } : false}
            disabled={disabled || loading}
            iconRender={renderUploadIcon}
        >
            {fileList.length < maxFileCount && (
                <div className="user-input-upload-trigger">
                    <CloudUploadOutlined className="user-input-upload-trigger-icon" />
                    <div className="user-input-upload-trigger-title">
                        Click or drag File to this area to upload
                    </div>
                    <div className="user-input-upload-trigger-desc">
                        You can upload images or PDFs up to 10MB each.
                    </div>
                </div>
            )}
        </Upload>
    );

    const renderHeader = () => {
        if (!headerStatus.open || !headerStatus.type) return null;
        const isLink = headerStatus.type === 'link';
        if (isLink) {
            return (
                <div className="user-input-inline-header">
                    <div className="attachment-header-bar">
                        <span className="attachment-header-title">Upload Attachment</span>
                        <AntdButton
                            className="attachment-header-close"
                            type="text"
                            icon={<CloseOutlined />}
                            aria-label="Close panel"
                            onClick={closeHeader}
                        />
                    </div>
                    <div className={`attachment-upload-body ${isMobile ? 'mobile' : ''}`}>
                        {uploadContent}
                    </div>
                </div>
            );
        }
        return null;
    };

    const renderTextarea = () => {
        if (showRecordingPlaceholder) {
            return (
                <Space className="user-input-recording-placeholder">
                    <Ring2 size="18" stroke="3" strokeLength="0.25" bgOpacity="0.1" speed="0.8" color={colorReferencePrimary || colorPrimary} />
                    <span>{recordingTip}</span>
                </Space>
            );
        }
        return (
            <textarea
                ref={inputRef}
                placeholder={placeholder}
                value={inputText}
                maxLength={maxLength}
                disabled={disabled}
                readOnly={loading}
                onChange={e => onInputChange(e.target.value, e)}
                onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        throttleSend();
                    }
                }}
                className="user-input-textarea"
                rows={1}
            />
        );
    };

    const renderSendButton = () => (
        <AntdButton
            className="user-input-send-btn"
            type="primary"
            disabled={sendButtonDisabled}
            loading={loading}
            onClick={throttleSend}
            aria-label="Send"
            icon={<SvgIcon name="arrow-up" aria-hidden="true" color={sendButtonDisabled ? colorTextDisabled : colorBtnText} />}
        />
    );

    useEffect(() => {
        if (!disabled) {
            inputRef.current?.focus();
        }
    }, [ disabled ]);

    useEffect(() => {
        setInputText(inputValue);
    }, [ inputValue ]);

    useEffect(() => {
        if (!recording) {
            setTempRecordingText(recordingText);
        }
    }, [ recordingText, recording ]);

    useEffect(() => {
        if (tempRecordingText) {
            setInputText(prev => prev + tempRecordingText);
            setTempRecordingText('');
        }
    }, [ tempRecordingText ]);

    useEffect(() => {
        if (!recording && headerStatus.type === 'mic') {
            closeHeader();
        }
    }, [ recording ]);

    useEffect(() => {
        if (inputRef.current) {
            const el = inputRef.current;
            el.style.height = 'auto';
            const next = Math.min(el.scrollHeight, 200);
            el.style.height = `${Math.max(next, 34)}px`;
        }
    }, [ inputText ]);

    // 主体内容和 footer
    return (
        <div className={`user-input ${curTheme}-user-input ${className}`} style={style}>
            {renderHeader()}
            <Flex className={`user-input-main ${isMobile ? 'mobile-layout' : ''}`} align="flex-end" gap={8}>
                <div className="input-section">
                    <div className="input-wrapper">
                        {renderTextarea()}
                    </div>
                </div>
            </Flex>
            {/* 右下角字数/附件数提示 + 操作 */}
            <div className="user-input-footer">
                <span className="user-input-footer-text">
                    Characters: {inputText.length}/{maxLength} | Files: {fileList.length}/{maxFileCount}
                </span>
                <Space align="center" className="user-input-footer-actions" size={4}>
                    <Tooltip title={`Upload Attachment (${fileList.length}/${maxFileCount})`}>
                        <AntdButton
                            className="user-input-btn"
                            type="text"
                            disabled={disabled || loading}
                            icon={<SvgIcon name="link" aria-hidden="true" />}
                            aria-label="Upload Attachment"
                            onClick={() => toggleHeader('link')}
                        />
                    </Tooltip>
                    <Tooltip title={recording ? 'Stop' : 'Voice Input'}>
                        <AntdButton
                            className="user-input-btn"
                            type="text"
                            disabled={disabled || loading}
                            icon={recording ? <Waveform size={16} stroke={2} color={colorReferencePrimary || colorPrimary} /> : <SvgIcon name="microphone" aria-hidden="true" />}
                            aria-label={recording ? 'Stop Voice Input' : 'Voice Input'}
                            onClick={handleRecordingToggle}
                        />
                    </Tooltip>
                    {renderSendButton()}
                </Space>
            </div>
        </div>
    );
};

export default UserInput;

